import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, Box, Typography, IconButton, Tabs, Tab, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import PatientOverview from 'components/PatientOverview';
import MaleInvestigation from 'components/MaleInvestigation';
import FemaleInvestigation from 'components/FemaleInvestigation';
import useFormDraft from '../../hooks/useFormDraft';
import { api } from '../../api';

function PatientInvestigations() {
  const { patientCode, coupleCode } = useParams();
  const navigate = useNavigate();

  // State management
  const [patientData, setPatientData] = useState(null);
  const [spouseData, setSpouseData] = useState(null);
  const [investigationParams, setInvestigationParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Load patient data and investigations on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!patientCode || !coupleCode || coupleCode === 'undefined') {
        Swal.fire({
          icon: 'error',
          title: 'Missing Information',
          text: 'Patient code and couple code are required'
        });
        return;
      }

      setLoading(true);

      try {
        // Load patient details
        const patientResponse = await api.contacts.getPatientDetails(patientCode);

        if (patientResponse?.data?.contact) {
          const patient = patientResponse.data.contact;
          const spouse = patientResponse.data?.spouse;

          const patientData = {
            id: patient.id,
            name: patient.full_name,
            external_code: patient.external_code,
            couple_code: patientResponse.data?.couple_info?.external_code,
            gender: patient.gender,
            phone_number: patientResponse.data?.phones[0]?.phone_number,
            email: patient.email,
            occupation: patient.occupation,
            nationality_id: patient.irm_countries_id,
            nationality: patient?.nationality,
            marital_status: patient.marital_status,
            married_for: patient.married_for,
            trying_for: patient.trying_for,
            profile_picture_base_url: patient.profile_picture_base_url,
            profile_picture_path: patient.profile_picture_path,
          };
          setPatientData(patientData);

          // Handle spouse data - only set if spouse exists
          let spouseData = null;

          if (spouse?.contact) {
            spouseData = {
              id: spouse.contact.id,
              name: spouse.contact.full_name,
              external_code: spouse.contact.external_code,
              couple_code: patientResponse.data?.couple_info?.external_code || '',
              gender: spouse.contact.gender,
              phone_number: spouse?.phones?.[0]?.phone_number || '',
              email: spouse.contact.email_address || '',
              occupation: spouse.contact.occupation || '',
              nationality_id: spouse.contact.irm_countries_id || '',
              nationality: spouse.contact.nationality || '',
              marital_status: spouse.contact.marital_status || '',
              married_for: spouse.contact.married_for || '',
              trying_for: spouse.contact.trying_for || '',
              profile_picture_base_url: spouse.contact.profile_picture_base_url || '',
              profile_picture_path: spouse.contact.profile_picture_path || '',
            };
          }
          setSpouseData(spouseData);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Patient Not Found',
            text: 'Patient not found with the provided code'
          });
        }

        // Load investigation parameters (for dropdowns and validation)
        const paramsResponse = await api.opd.getHistoryParams();
        if (paramsResponse?.data) {
          setInvestigationParams(paramsResponse.data);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Load Failed',
          text: error.response?.data?.message || 'Failed to load patient data'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patientCode, coupleCode]);

  const onSaveInvestigation = async (data) => {
    setSaving(true);

    try {
      const investigationData = [...data];

      console.log('Saving investigation:', investigationData);

      const response = await api.opd.createContactHistory(investigationData);

      if (response?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Investigation saved successfully!'
        });
      }
    } catch (error) {
      console.error('Error saving investigation:', error);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: error.response?.data?.message || 'Failed to save investigation'
      });
    } finally {
      setSaving(false);
    }
  };

  const onFetchContactHistory = async (contactId) => {
    try {
      console.log('Fetching contact history for contact ID:', contactId);
      const response = await api.opd.getContactHistory(contactId);

      if (response?.data) {
        console.log('Contact history fetched successfully:', response.data);
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching contact history:', error);
      
      // Show user-friendly error message
      if (error.response?.status === 404) {
        console.log('No contact history found for this patient');
        return { data: { contact_histories: [] } }; // Return empty history structure
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: error.response?.data?.message || 'Failed to load investigation history'
      });
      throw error;
    }
  };

  const onDeleteHistory = async (historyItem) => {
    try {
      console.log('Deleting history record:', historyItem);
      
      // Prepare delete payload according to API specification
      const deletePayload = {
        contact_id: historyItem.contact_id,
        param_group_id: historyItem.param_group_id,
        param_group_member_id: historyItem.param_group_member_id,
        contact_history_id: historyItem.id
      };
      
      console.log('Delete payload:', deletePayload);
      const response = await api.opd.deleteContactHistory(deletePayload);

      if (response?.data) {
        console.log('History record deleted successfully');
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          text: 'Investigation record deleted successfully!',
          timer: 3000,
          showConfirmButton: false
        });
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error deleting history record:', error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.response?.data?.message || 'Failed to delete investigation record'
      });
      throw error;
    }
  };  const handleBack = () => {
    navigate('/patient-list');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      onClick={handleBack}
                      sx={{
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <MDTypography variant="h6" color="white">
                      Patient Investigations - {patientData?.name || patientCode}
                    </MDTypography>
                  </Box>
                  
                  {/* Quick Navigation Buttons */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip 
                      title="Manage Contact Histories" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/contact-histories/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <HistoryIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="View Prescriptions" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/prescription/patient/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <DescriptionIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="Create Prescription" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/prescription/create/draft/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <AddIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip 
                      title="Infertility Summary" 
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            fontSize: '0.75rem',
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={() => navigate(`/infertility-summary/${patientCode}/${coupleCode}`)}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <AssignmentIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </MDBox>

              {/* Patient Overview Component */}
              <MDBox px={3} pb={2}>
                <PatientOverview patientData={patientData} spouseData={spouseData} />
              </MDBox>

              {/* Investigation Tabs - Patient and Spouse */}
              <MDBox px={3}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="investigation tabs"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: '48px',
                      fontSize: '0.95rem',
                      fontWeight: 'medium',
                    },
                    '& .Mui-selected': {
                      color: '#00798E !important',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#00798E',
                      height: 3,
                    },
                  }}
                >
                  <Tab
                    icon={<PersonIcon />}
                    iconPosition="start"
                    label={`${patientData?.name || 'Patient'} Investigation`}
                    sx={{ gap: 1 }}
                  />
                  {spouseData && (
                    <Tab
                      icon={<PeopleIcon />}
                      iconPosition="start"
                      label={`${spouseData?.name || 'Spouse'} Investigation`}
                      sx={{ gap: 1 }}
                    />
                  )}
                </Tabs>
              </MDBox>

              {/* Investigation Content */}
              <MDBox p={3}>
                {/* Patient Investigation Tab */}
                {tabValue === 0 && patientData && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#00798E', mb: 3 }}>
                      {patientData.name} - Investigation Details
                    </Typography>
                    {patientData.gender === 'M' ? (
                      <MaleInvestigation
                        patientData={patientData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    ) : (
                      <FemaleInvestigation
                        patientData={patientData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    )}
                  </Box>
                )}

                {/* Spouse Investigation Tab */}
                {tabValue === 1 && spouseData && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#00798E', mb: 3 }}>
                      {spouseData.name} - Investigation Details
                    </Typography>
                    {spouseData.gender === 'M' ? (
                      <MaleInvestigation
                        patientData={spouseData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    ) : (
                      <FemaleInvestigation
                        patientData={spouseData}
                        investigationParams={investigationParams}
                        onSaveInvestigation={onSaveInvestigation}
                        onFetchContactHistory={onFetchContactHistory}
                        onDeleteHistory={onDeleteHistory}
                        saving={saving}
                      />
                    )}
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default PatientInvestigations;
